import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { OBSERVATION_CAPTURE_LABELS as L } from '@/components/widget/observation-capture/labels';
import { ObservationCapture } from '@/components/widget/observation-capture/observation-capture';
import { useNavigateWithContentFlush } from '@/hooks';
import { CANONICAL_LABELS } from '@/lib/content-labels';
import { useCaptureFlowStore } from '@/store/useCaptureFlowStore';
import { toContentHref } from '@/utils/content-ref';

interface CreateObservationCaptureDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultMode: 'recording' | 'photo' | 'video';
}

// A dialog session mirrors the chat card's: one open dialog = one observation. The
// dialog stays open across submits (the widget keeps its link target across rounds),
// the first submit starts the group and later submits join it, and an explicit Done
// closes the dialog and navigates to the first created item. Closing by any route
// (Done, ESC, backdrop) resets the session here, so reopening starts a fresh
// observation; the widget itself resets via its open-keyed remount.
export function CreateObservationCaptureDialog({
  open,
  onOpenChange,
  defaultMode,
}: CreateObservationCaptureDialogProps) {
  const navigate = useNavigateWithContentFlush();
  // One open dialog is one session: minted once so it survives re-render, and retired on
  // close so the next open starts a fresh observation.
  const [sessionId, setSessionId] = useState(() => `cap-dialog-${crypto.randomUUID()}`);
  const [firstCreatedId, setFirstCreatedId] = useState<number | null>(null);
  const endSession = useCaptureFlowStore((state) => state.endSession);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      endSession(sessionId);
      setSessionId(`cap-dialog-${crypto.randomUUID()}`);
      setFirstCreatedId(null);
    }
    onOpenChange(next);
  };

  const handleDone = () => {
    const target = firstCreatedId;
    handleOpenChange(false);
    if (target !== null) {
      navigate(toContentHref(String(target)));
    }
  };

  const title =
    defaultMode === 'photo'
      ? `New ${CANONICAL_LABELS.photo}`
      : defaultMode === 'video'
        ? `New ${CANONICAL_LABELS.video}`
        : `New ${CANONICAL_LABELS.recording}`;

  // A video is stored as-is, so the generated-metadata promise is true of audio and
  // photos only and must not be made on the video door.
  const description =
    defaultMode === 'video'
      ? 'Record or upload video, and optionally link it to existing content.'
      : 'Record audio or capture photos, and optionally link them to existing content. Titles and descriptions are generated in the background.';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-2">
          <ObservationCapture
            sessionId={sessionId}
            defaultMode={defaultMode}
            onSubmitted={({ photoIds, recordingIds, videoIds }) => {
              setFirstCreatedId(
                (prev) => prev ?? photoIds[0] ?? recordingIds[0] ?? videoIds[0] ?? null
              );
            }}
          />
        </DialogBody>
        {firstCreatedId !== null ? (
          <DialogFooter>
            <Button type="button" variant="secondary" size="sm" onClick={handleDone}>
              {L.session.done}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
